import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { PresidentialShell } from "./DirectorApply";
import { DirectorKYCStep } from "../components/director/DirectorKYCStep";

/** /director/kyc?invite=<token> — eKYC document upload scoped to the founder's invitation session. */
export default function DirectorKYC() {
  const { user, api } = useAuth();
  const loading = user === null; // AuthContext: null = checking, false = guest
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("invite") || "";
  const [invite, setInvite] = useState(null);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!token) { setChecked(true); return; }
    api.get(`/director/invites/${token}`).then((r) => setInvite(r.data)).catch(() => setInvite(null)).finally(() => setChecked(true));
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading || !checked) {
    return <PresidentialShell><div className="text-center text-[#8A8A82] font-body py-16"><Loader2 className="mx-auto animate-spin text-[#C9A961]" /> Preparing your eKYC…</div></PresidentialShell>;
  }
  // Not signed in → the invitation flow creates the account first, then returns to eKYC automatically.
  if (!user) return <Navigate to={token ? `/director/apply?invite=${token}` : "/login?next=/director/kyc"} replace />;
  if (user.role !== "director") return <Navigate to={token ? `/director/apply?invite=${token}` : "/director/apply"} replace />;
  // Token issued to a different founder than the one signed in — keep sessions strictly scoped.
  if (invite && invite.email && user.email && invite.email.toLowerCase() !== user.email.toLowerCase()) {
    return (
      <PresidentialShell>
        <div className="max-w-xl mx-auto text-center" data-testid="director-kyc-wrong-session">
          <h1 className="font-serif text-3xl text-[#F5F5F0]">This invitation belongs to another founder</h1>
          <p className="font-body text-[#8A8A82] mt-4">You are signed in as {user.email}. Please sign out and open the link from the invited mailbox.</p>
          <Link to="/account" className="mt-8 inline-block border border-[#C9A961]/50 text-[#C9A961] px-6 py-3 font-grotesk text-[10px] uppercase tracking-[0.26em]">Back to account</Link>
        </div>
      </PresidentialShell>
    );
  }
  return (
    <PresidentialShell>
      <div data-testid="director-kyc-page">
        <DirectorKYCStep api={api} allowContinue onVerifiedOrContinue={() => navigate(token ? `/director/apply?invite=${token}` : "/director/apply")} />
      </div>
    </PresidentialShell>
  );
}
